import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  AlertTriangle, Plus, X, Loader2, Calendar, User,
  CheckCircle, Clock, Activity, HeartPulse
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

export default function InjuryTracker({ childId, teamId, user }) {
  const [injuries, setInjuries] = useState([]);
  const [teamStatus, setTeamStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedInjury, setSelectedInjury] = useState(null);
  const [injuryTypes, setInjuryTypes] = useState([]);
  const [bodyParts, setBodyParts] = useState([]);
  
  const [newInjury, setNewInjury] = useState({
    child_id: childId || '',
    injury_type: '',
    body_part: '',
    side: 'n/a',
    severity: 'minor',
    injury_date: new Date().toISOString().split('T')[0],
    description: '',
    medical_attention: false
  });

  useEffect(() => {
    fetchInjuryTypes();
    if (childId) {
      fetchAthleteInjuries();
    } else if (teamId) {
      fetchTeamStatus();
    }
  }, [childId, teamId]);

  const fetchInjuryTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/injuries/types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInjuryTypes(response.data.injury_types || []);
      setBodyParts(response.data.body_parts || []);
    } catch (err) {
      console.error('Failed to fetch injury types:', err);
    }
  };

  const fetchAthleteInjuries = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/injuries/athlete/${childId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInjuries(response.data.injuries || []);
    } catch (err) {
      console.error('Failed to fetch injuries:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamStatus = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/injuries/team/${teamId}/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeamStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch team status:', err);
    } finally {
      setLoading(false);
    }
  };

  const logInjury = async () => {
    if (!newInjury.injury_type || !newInjury.body_part) {
      toast.error('Injury type and body part are required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/injuries/log`, newInjury, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Injury logged');
      setShowAddModal(false);
      if (childId) fetchAthleteInjuries();
      if (teamId) fetchTeamStatus();
    } catch (err) {
      toast.error('Failed to log injury');
    }
  };

  const clearAthlete = async (injuryId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/injuries/${injuryId}/clear`, {
        clearance_type: 'full',
        notes: 'Cleared to return to full activity'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Athlete cleared to play');
      if (childId) fetchAthleteInjuries();
      if (teamId) fetchTeamStatus();
    } catch (err) {
      toast.error('Failed to clear athlete');
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'minor': return 'bg-yellow-500/20 text-yellow-400';
      case 'moderate': return 'bg-orange-500/20 text-orange-400';
      case 'serious': return 'bg-red-500/20 text-red-400';
      case 'severe': return 'bg-red-600/20 text-red-500';
      default: return 'bg-zinc-700 text-zinc-400';
    }
  };

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6" data-testid="injury-tracker">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-red-500/20 to-orange-500/20 rounded-xl flex items-center justify-center">
            <HeartPulse className="h-5 w-5 text-red-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Injury Management</h3>
            <p className="text-xs text-zinc-500">Track & manage injuries</p>
          </div>
        </div>
        <Button
          onClick={() => setShowAddModal(true)}
          className="bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm"
          data-testid="log-injury-btn"
        >
          <Plus className="h-4 w-4 mr-1" />
          Log Injury
        </Button>
      </div>

      {/* Team Status View */}
      {teamStatus && (
        <div className="mb-6">
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-green-400">{teamStatus.available_count}</p>
              <p className="text-xs text-zinc-400">Available</p>
            </div>
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-red-400">{teamStatus.injured_count}</p>
              <p className="text-xs text-zinc-400">Injured</p>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-amber-400">{teamStatus.returning_this_week}</p>
              <p className="text-xs text-zinc-400">Returning Soon</p>
            </div>
          </div>

          {teamStatus.injured_athletes?.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-zinc-400">Currently Injured</h4>
              {teamStatus.injured_athletes.map(athlete => (
                <div key={athlete.injury_id} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                  <div>
                    <p className="text-white font-medium">{athlete.athlete_name}</p>
                    <p className="text-xs text-zinc-500">
                      {athlete.injury_type} ({athlete.body_part}) • Est. return: {athlete.estimated_return}
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs ${getSeverityColor(athlete.severity)}`}>
                    {athlete.severity}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Individual Injuries List */}
      {childId && (
        <div className="space-y-2 max-h-[300px] overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-red-400" />
            </div>
          ) : injuries.length === 0 ? (
            <div className="text-center py-8 text-zinc-500">
              <CheckCircle className="h-12 w-12 mx-auto mb-3 opacity-50 text-green-400" />
              <p>No injury history</p>
            </div>
          ) : (
            injuries.map(injury => (
              <div
                key={injury.id}
                className={`p-4 rounded-xl border ${
                  injury.status === 'active' 
                    ? 'bg-red-500/10 border-red-500/30' 
                    : 'bg-zinc-800/50 border-zinc-700/50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-white capitalize">
                        {injury.injury_type?.replace('_', ' ')}
                      </p>
                      <span className={`px-2 py-0.5 rounded text-xs ${getSeverityColor(injury.severity)}`}>
                        {injury.severity}
                      </span>
                    </div>
                    <p className="text-xs text-zinc-500 mt-1">
                      {injury.body_part} {injury.side !== 'n/a' && `(${injury.side})`} • {injury.injury_date}
                    </p>
                    {injury.description && (
                      <p className="text-sm text-zinc-400 mt-2">{injury.description}</p>
                    )}
                  </div>
                  {injury.status === 'active' && user?.role === 'coach' && (
                    <Button
                      size="sm"
                      onClick={() => clearAthlete(injury.id)}
                      className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Clear
                    </Button>
                  )}
                </div>
                {injury.estimated_return_date && injury.status === 'active' && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-zinc-500">
                    <Clock className="h-3 w-3" />
                    Est. return: {injury.estimated_return_date}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Add Injury Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-md p-6 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-white">Log Injury</h3>
              <button onClick={() => setShowAddModal(false)} className="text-zinc-500 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <select
                value={newInjury.injury_type}
                onChange={e => setNewInjury({...newInjury, injury_type: e.target.value})}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg p-3 text-white"
              >
                <option value="">Select Injury Type</option>
                {injuryTypes.map(type => (
                  <option key={type} value={type}>{type.replace('_', ' ')}</option>
                ))}
              </select>

              <select
                value={newInjury.body_part}
                onChange={e => setNewInjury({...newInjury, body_part: e.target.value})}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg p-3 text-white"
              >
                <option value="">Select Body Part</option>
                {bodyParts.map(part => (
                  <option key={part} value={part}>{part}</option>
                ))}
              </select>

              <select
                value={newInjury.side}
                onChange={e => setNewInjury({...newInjury, side: e.target.value})}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg p-3 text-white"
              >
                <option value="n/a">Not Applicable</option>
                <option value="left">Left</option>
                <option value="right">Right</option>
                <option value="both">Both</option>
              </select>

              <select
                value={newInjury.severity}
                onChange={e => setNewInjury({...newInjury, severity: e.target.value})}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg p-3 text-white"
              >
                <option value="minor">Minor (1-7 days)</option>
                <option value="moderate">Moderate (1-4 weeks)</option>
                <option value="serious">Serious (1-3 months)</option>
                <option value="severe">Severe (3+ months)</option>
              </select>

              <Input
                type="date"
                value={newInjury.injury_date}
                onChange={e => setNewInjury({...newInjury, injury_date: e.target.value})}
                className="bg-zinc-800 border-zinc-700 text-white"
              />

              <textarea
                placeholder="Description (how did the injury occur?)"
                value={newInjury.description}
                onChange={e => setNewInjury({...newInjury, description: e.target.value})}
                rows={3}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg p-3 text-white resize-none"
              />

              <label className="flex items-center gap-2 text-sm text-zinc-400">
                <input
                  type="checkbox"
                  checked={newInjury.medical_attention}
                  onChange={e => setNewInjury({...newInjury, medical_attention: e.target.checked})}
                  className="rounded bg-zinc-800 border-zinc-700"
                />
                Medical attention received
              </label>

              <Button
                onClick={logInjury}
                className="w-full bg-gradient-to-r from-red-500 to-orange-500 text-white"
              >
                Log Injury
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
